import React from 'react';

import { AccountOverviewContent, AccountsGroupLoader, ErrorView } from '..';
import { useConfig } from '../../../../defaultConfig';
import { accountListSelector, useAccounts } from '../../../../fetchHooks/useAccount';
import { useAccountsGroupsQuery } from '../../../../fetchHooks/useAccountsGroups';
import { useAccountsPropertiesQuery } from '../../../../fetchHooks/useAccountsProperties';
import { useCreditcards } from '../../../../fetchHooks/useCards';
import useTranslation from '../../../../hooks/useTranslation';
import { simulateMissingGroups } from '../../../../services/accountsGroupsService';
import { getGroupedAccounts } from '../../../EditAccountOverview/components/DragAndDropAccountsList/components/GroupedSortableDnd/components/DndContextProvider/utils';
import mapAllAccountCardItems from '../../../EditAccountOverview/components/DragAndDropAccountsList/mapAccountCardItems';
import { NoAccountsView } from '../NoAccountsView';

export const GroupedAccountsOverview = () => {
    const translation = useTranslation();
    const { featureToggles } = useConfig();

    const { data: accounts = [], ...accountsQuery } = useAccounts({ select: accountListSelector });
    const accountNumbers = accounts.map((account) => account.accountNumber.value) ?? [];
    const { data: cards = [] } = useCreditcards(accountNumbers, featureToggles.enableCreditCardsFrontpage);
    const { data: accountGroups = [], ...groupsQuery } = useAccountsGroupsQuery();
    const { data: accountProperties = [], ...propertiesQuery } = useAccountsPropertiesQuery();

    if (accountsQuery.error) {
        return <ErrorView error={accountsQuery.error} refresh={accountsQuery.refetch} />;
    }

    if (groupsQuery.error) {
        return <ErrorView error={groupsQuery.error} refresh={groupsQuery.refetch} />;
    }

    if (propertiesQuery.error) {
        return <ErrorView error={propertiesQuery.error} refresh={propertiesQuery.refetch} />;
    }

    if (
        accountsQuery.isLoading ||
        groupsQuery.isLoading ||
        propertiesQuery.isLoading ||
        accountsQuery.isRefetching ||
        groupsQuery.isRefetching ||
        propertiesQuery.isRefetching
    ) {
        return <AccountsGroupLoader numberOfItems={accounts.length} />;
    }

    if (accountsQuery.isSuccess && accounts.length === 0) {
        return <NoAccountsView />;
    }

    const groupedAccounts = getGroupedAccounts(
        mapAllAccountCardItems(accounts, cards),
        simulateMissingGroups(accountGroups, translation.accountOverview.headingMobile),
        accountProperties,
    ).filter((group) => group.values.length !== 0);

    const onlyGroupsWithFavoriteAccounts = groupedAccounts.filter((group) =>
        group.values.some((account) => account.favorite),
    );

    const filteredGroups =
        onlyGroupsWithFavoriteAccounts.length === 0 ? [groupedAccounts[0]] : onlyGroupsWithFavoriteAccounts;

    return (
        <>
            {filteredGroups.map((group, index) => (
                <AccountOverviewContent
                    key={group.id}
                    accountCardItems={group.values}
                    header={group.name}
                    showHasMore={index === filteredGroups.length - 1}
                />
            ))}
        </>
    );
};
